import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { getLoginUrl } from "@/const";
import {
  ArrowRight,
  Award,
  BarChart3,
  Calendar,
  CheckCircle,
  ChevronRight,
  ClipboardCheck,
  ClipboardList,
  ListOrdered,
  Clock,
  FileText,
  Laptop,
  Medal,
  Menu,
  Monitor,
  Music,
  School,
  Shield,
  Star,
  TabletSmartphone,
  Timer,
  Trophy,
  UserCheck,
  Users,
  Wifi,
  X,
  Zap,
  LogIn,
} from "lucide-react";
import { Link, useSearch } from "wouter";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { trpc } from "@/lib/trpc";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

/** Декоративная «лента» — отсылка к художественной гимнастике. */
function Ribbon({ className = "" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 800 400" fill="none" aria-hidden="true" preserveAspectRatio="none">
      <defs>
        <linearGradient id="lpRibbon" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="rgba(139,122,246,0)" />
          <stop offset="45%" stopColor="rgba(139,122,246,0.55)" />
          <stop offset="100%" stopColor="rgba(139,122,246,0)" />
        </linearGradient>
      </defs>
      <path d="M-40 160 C 160 20, 280 300, 460 170 S 720 90, 860 280" stroke="url(#lpRibbon)" strokeWidth="2.5" />
      <path d="M-40 240 C 180 140, 320 380, 500 250 S 740 200, 880 360" stroke="url(#lpRibbon)" strokeWidth="1.4" opacity="0.6" />
    </svg>
  );
}

function SectionHead({ eyebrow, title, subtitle, center = true }: { eyebrow: string; title: string; subtitle?: string; center?: boolean }) {
  return (
    <div className={`${center ? "text-center mx-auto" : ""} max-w-2xl mb-12 sm:mb-16`}>
      <span className="eyebrow">{eyebrow}</span>
      <h2 className="lp-display text-3xl sm:text-4xl md:text-5xl mb-4 leading-tight">{title}</h2>
      {subtitle && <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">{subtitle}</p>}
    </div>
  );
}

// Информационные партнёры в подвале главной
const PARTNERS = [
  { url: "https://roqeta.com", labelKey: "home.footer.partnerRoqeta" },
];

export default function Home() {
  const { t, i18n } = useTranslation("pages");
  const { isAuthenticated } = useAuth();
  const searchString = useSearch();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Соревнования платформы: сначала идущие и будущие (ближайшие первыми),
  // затем недавно прошедшие — чтобы блок не пустовал в межсезонье.
  const { data: allTournaments } = trpc.tournaments.list.useQuery({ limit: 120 });
  const showcase = (() => {
    const rows = (allTournaments || []) as any[];
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const ms = (d: any) => new Date(d).getTime();
    const upcoming = rows
      .filter(r => ms(r.endDate) >= today.getTime())
      .sort((a, b) => ms(a.startDate) - ms(b.startDate));
    const past = rows
      .filter(r => ms(r.endDate) < today.getTime())
      .sort((a, b) => ms(b.startDate) - ms(a.startDate));
    return [...upcoming, ...past].slice(0, 6);
  })();
  const formatRange = (from: any, to: any) => {
    const f = new Date(from), tt = new Date(to);
    const fmt = (d: Date) => d.toLocaleDateString(i18n.language, { day: "2-digit", month: "short" });
    return f.toDateString() === tt.toDateString()
      ? `${fmt(f)} ${tt.getFullYear()}`
      : `${fmt(f)} — ${fmt(tt)} ${tt.getFullYear()}`;
  };

  useEffect(() => {
    const params = new URLSearchParams(searchString);
    const authError = params.get("auth_error");
    if (authError) {
      toast.error(decodeURIComponent(authError));
      window.history.replaceState({}, "", "/");
    }
  }, [searchString]);

  const navLinks = (
    <>
      <a href="#competitions" className="text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap">{t("home.nav.competitions")}</a>
      <a href="#how-it-works" className="text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap">{t("home.nav.howItWorks")}</a>
      <a href="#pricing" className="text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap">{t("home.nav.pricing")}</a>
      <Link href="/knowledge" className="text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap">{t("home.nav.knowledge")}</Link>
    </>
  );

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      {/* ───────── Header ───────── */}
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="container flex items-center justify-between h-16">
          <Link href="/" className="flex items-center gap-2 shrink-0">
            <img src="/logo.png" alt="JUDIX" className="h-8 sm:h-9 w-auto" />
            <span className="lp-display text-xl sm:text-2xl font-semibold tracking-tight">JUDIX</span>
          </Link>
          <nav className="hidden lg:flex items-center gap-6 xl:gap-8 text-sm">{navLinks}</nav>
          <div className="hidden lg:flex items-center gap-2 xl:gap-3">
            <LanguageSwitcher localOnly />
            {isAuthenticated ? (
              <Link href="/dashboard"><Button size="sm" className="xl:h-10 xl:px-4">{t("home.nav.cabinet")}</Button></Link>
            ) : (
              <>
                <a href={getLoginUrl()}><Button variant="outline" size="sm" className="xl:h-10 xl:px-4 border-primary/40 text-primary hover:bg-primary/10"><LogIn className="w-4 h-4 mr-1.5" />{t("home.nav.login")}</Button></a>
                <Link href="/register"><Button size="sm" className="xl:h-10 xl:px-4 glow-primary">{t("home.nav.register")}</Button></Link>
              </>
            )}
          </div>
          <div className="lg:hidden flex items-center gap-1 shrink-0">
            <LanguageSwitcher localOnly />
            {isAuthenticated ? (
              <Link href="/dashboard"><Button size="sm" className="h-9 px-3">{t("home.nav.cabinetShort")}</Button></Link>
            ) : (
              <a href={getLoginUrl()}><Button variant="outline" size="sm" className="h-9 px-3 border-primary/40 text-primary"><LogIn className="w-4 h-4 mr-1" />{t("home.nav.login")}</Button></a>
            )}
            <button
              className="h-10 w-10 flex items-center justify-center rounded-lg hover:bg-accent/50 transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-border/50 bg-background/95 backdrop-blur">
            <div className="container py-4 flex flex-col gap-3 [&>a]:py-2 [&>a]:text-muted-foreground" onClick={() => setMobileMenuOpen(false)}>
              {navLinks}
              <div className="flex flex-col gap-2 pt-2 border-t border-border/50">
                {isAuthenticated ? (
                  <Link href="/dashboard"><Button className="w-full">{t("home.nav.cabinet")}</Button></Link>
                ) : (
                  <>
                    <a href={getLoginUrl()}><Button variant="outline" className="w-full">{t("home.nav.login")}</Button></a>
                    <Link href="/register"><Button className="w-full">{t("home.nav.register")}</Button></Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </header>

      {/* ───────── Hero ───────── */}
      <section className="lp-bg lp-grain pt-28 sm:pt-36 pb-16 sm:pb-24 relative">
        <div className="lp-mesh" />
        <Ribbon className="lp-ribbon pointer-events-none absolute -top-6 left-0 w-[140%] h-[420px] -z-[1] opacity-70" />
        <div className="container relative">
          <div className="max-w-4xl mx-auto text-center reveal-up">
            <h1 className="lp-display text-[2.1rem] sm:text-5xl md:text-6xl lg:text-[4rem] font-semibold mb-6 leading-[1.08] sm:leading-[1.02] text-balance [hyphens:none]">
              {t("home.hero.titlePart1")}{" "}
              <span className="lp-shine">{t("home.hero.titleHighlight")}</span>{" "}
              <span className="font-normal italic text-muted-foreground">{t("home.hero.titlePart2")}</span>
            </h1>
            <p className="text-base sm:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto leading-relaxed">
              {t("home.hero.subtitle")}
            </p>
            {/* Главное действие первого экрана: люди приходят сюда за своим
                турниром — календарь, а не рассказ о платформе. */}
            <div className="flex flex-col items-center gap-3">
              <Link href="/tournaments">
                <Button size="lg" className="text-base sm:text-lg px-8 sm:px-10 h-14 rounded-xl glow-primary glow-primary-hover">
                  <Calendar className="w-5 h-5 mr-2.5" />
                  {t("home.hero.chooseCompetition")}
                  <ChevronRight className="w-5 h-5 ml-1.5" />
                </Button>
              </Link>
              {isAuthenticated ? (
                <Link href="/dashboard">
                  <Button variant="outline" size="lg" className="h-12 px-6 rounded-xl border-primary/40 text-primary hover:bg-primary/10">
                    {t("home.nav.cabinet")}
                    <ChevronRight className="w-4 h-4 ml-1.5" />
                  </Button>
                </Link>
              ) : (
                <a href={getLoginUrl()}>
                  <Button variant="outline" size="lg" className="h-12 px-6 rounded-xl border-primary/40 text-primary hover:bg-primary/10">
                    <LogIn className="w-4 h-4 mr-2" />
                    {t("home.hero.loginButton")}
                  </Button>
                </a>
              )}
            </div>
          </div>

          {/* Value props — что берём на себя, по шагам турнира. */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-14 sm:mt-20 max-w-5xl mx-auto reveal-stagger">
            {[
              { icon: Users, key: "entries" },
              { icon: ListOrdered, key: "protocols" },
              { icon: TabletSmartphone, key: "judging" },
              { icon: Monitor, key: "tech" },
            ].map((stat) => (
              <div
                key={stat.key}
                className="lp-card p-5 sm:p-6 text-center"
              >
                <span className="lp-chip w-11 h-11 mx-auto mb-3">
                  <stat.icon className="w-5 h-5 text-primary" />
                </span>
                <div className="text-sm sm:text-base font-semibold leading-snug mb-1">
                  {t(`home.valueProps.${stat.key}Title`)}
                </div>
                <div className="text-xs text-muted-foreground leading-snug">
                  {t(`home.valueProps.${stat.key}Label`)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="container"><hr className="lp-rule" /></div>

      {/* ───────── Наши партнёры: турниры платформы ───────── */}
      <section id="competitions" className="py-12 sm:py-16">
        <div className="container">
          <div className="text-center max-w-2xl mx-auto mb-8 sm:mb-10">
            <span className="eyebrow">{t("home.competitions.eyebrow")}</span>
            <h2 className="lp-display text-2xl sm:text-3xl mb-2 leading-tight">{t("home.competitions.title")}</h2>
            <p className="text-muted-foreground text-sm sm:text-base">{t("home.competitions.subtitle")}</p>
          </div>
          {showcase.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 max-w-5xl mx-auto reveal-stagger">
              {showcase.map((tournament: any) => (
                <Link
                  key={tournament.id}
                  href={`/tournament/${tournament.id}`}
                  className="group block"
                >
                  <div
                    className="relative overflow-hidden rounded-xl border border-border bg-gradient-to-br from-primary/20 via-primary/5 to-background transition-shadow group-hover:shadow-md"
                    style={{ aspectRatio: "3 / 4" }}
                  >
                    {tournament.posterUrl ? (
                      <img
                        src={tournament.posterUrl}
                        alt={tournament.name}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                      />
                    ) : (
                      <div className="flex h-full w-full flex-col items-center justify-center gap-2 p-3 text-center">
                        <Trophy className="h-6 w-6 text-primary/70" />
                        <span className="text-xs font-semibold leading-tight line-clamp-3">{tournament.name}</span>
                      </div>
                    )}
                  </div>
                  <div className="mt-2 text-[11px] sm:text-[13px] font-medium leading-snug line-clamp-2 group-hover:text-primary transition-colors">
                    {tournament.name}
                  </div>
                  <div className="text-[10px] sm:text-[11px] text-muted-foreground leading-snug">
                    {formatRange(tournament.startDate, tournament.endDate)}
                  </div>
                </Link>
              ))}
            </div>
          )}
          <div className="text-center mt-8">
            <Link href="/tournaments">
              <Button variant="outline" size="sm">
                {t("home.competitions.all")}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ───────── How it works ───────── */}
      <section id="how-it-works" className="lp-bg lp-grain py-16 sm:py-24 relative">
        <div className="lp-mesh opacity-60" />
        <div className="container relative">
          <SectionHead eyebrow={t("home.howItWorks.eyebrow")} title={t("home.howItWorks.title")} subtitle={t("home.howItWorks.subtitle")} />
          <div className="max-w-5xl mx-auto grid sm:grid-cols-5 gap-6 sm:gap-4 reveal-stagger">
            {[
              { step: "1", title: t("home.howItWorks.step1Title"), desc: t("home.howItWorks.step1Desc"), icon: Calendar },
              { step: "2", title: t("home.howItWorks.step2Title"), desc: t("home.howItWorks.step2Desc"), icon: ClipboardCheck },
              { step: "3", title: t("home.howItWorks.step3Title"), desc: t("home.howItWorks.step3Desc"), icon: FileText },
              { step: "4", title: t("home.howItWorks.step4Title"), desc: t("home.howItWorks.step4Desc"), icon: Laptop },
              { step: "5", title: t("home.howItWorks.step5Title"), desc: t("home.howItWorks.step5Desc"), icon: Trophy },
            ].map((item, i) => (
              <div key={item.step} className="text-center relative group">
                <div className="w-16 h-16 rounded-2xl gradient-primary flex items-center justify-center mx-auto mb-3 transition-transform group-hover:scale-110 group-hover:shadow-[0_0_30px_-5px_var(--primary)]">
                  <item.icon className="w-7 h-7 text-white" />
                </div>
                <div className="lp-display text-xs text-primary mb-1 tracking-wider">{t("home.howItWorks.stepLabel", { step: item.step })}</div>
                <h3 className="font-semibold text-sm mb-1">{item.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{item.desc}</p>
                {i < 4 && (
                  <div className="hidden sm:block absolute top-8 left-[calc(50%+32px)] w-[calc(100%-64px)]">
                    <div className="border-t-2 border-dashed border-primary/30" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────── Pricing ───────── */}
      <section id="pricing" className="py-16 sm:py-24">
        <div className="container">
          <SectionHead eyebrow={t("home.pricing.eyebrow")} title={t("home.pricing.title")} subtitle={t("home.pricing.subtitle")} />
          <div className="max-w-2xl mx-auto mb-8 reveal-up">
            <div className="lp-card lp-card-feature p-8 sm:p-10 text-center">
              <div className="flex items-end justify-center gap-2 mb-3">
                <span className="lp-shine lp-display text-6xl sm:text-7xl font-extrabold leading-none">{t("home.pricing.mainPrice")}</span>
                <span className="text-muted-foreground text-base sm:text-lg mb-2">{t("home.pricing.mainUnit")}</span>
              </div>
              <p className="text-muted-foreground text-sm sm:text-base max-w-md mx-auto mb-8 leading-relaxed">{t("home.pricing.mainDesc")}</p>
              <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5 text-left max-w-xl mx-auto mb-8">
                {(t("home.pricing.mainFeatures", { returnObjects: true }) as string[]).map((item) => (
                  <li key={item} className="flex items-start gap-2.5">
                    <CheckCircle className="w-[18px] h-[18px] text-primary shrink-0 mt-0.5" />
                    <span className="text-sm text-muted-foreground leading-snug">{item}</span>
                  </li>
                ))}
              </ul>
              <Link href="/register">
                <Button size="lg" className="px-10 h-12 glow-primary glow-primary-hover">
                  {t("home.pricing.choose")}
                  <ChevronRight className="w-5 h-5 ml-1.5" />
                </Button>
              </Link>
              <div className="mt-5 text-sm text-muted-foreground">
                {t("home.pricing.customLine")}{" "}
                <a href="mailto:judixkz@gmail.com" className="text-primary hover:underline font-medium">{t("home.pricing.contact")}</a>
              </div>
            </div>
          </div>

          {/* Аренда планшетов */}
          <div className="max-w-2xl mx-auto text-center">
            <div className="inline-flex items-center gap-3 px-5 py-3 rounded-xl lp-card">
              <TabletSmartphone className="w-5 h-5 text-primary shrink-0" />
              <div className="text-left">
                <span className="text-sm font-medium">{t("home.pricing.rentalLabel")}</span>
                <span className="text-sm text-muted-foreground">{t("home.pricing.rentalValue")}</span>
              </div>
            </div>
          </div>

          {/* Бесплатные возможности */}
          <div className="max-w-3xl mx-auto mt-8 p-5 rounded-xl bg-primary/5 border border-primary/15">
            <div className="flex items-start gap-3">
              <Star className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-sm mb-1">{t("home.pricing.freeTitle")}</h4>
                <p className="text-sm text-muted-foreground">{t("home.pricing.freeDesc")}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───────── Advantages ───────── */}
      <section className="lp-bg lp-grain py-16 sm:py-24 relative">
        <div className="lp-mesh opacity-60" />
        <div className="container relative">
          <SectionHead eyebrow={t("home.advantages.eyebrow")} title={t("home.advantages.title")} />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto reveal-stagger">
            {[
              { icon: Zap, title: t("home.advantages.item1Title"), desc: t("home.advantages.item1Desc") },
              { icon: Shield, title: t("home.advantages.item2Title"), desc: t("home.advantages.item2Desc") },
              { icon: Music, title: t("home.advantages.item3Title"), desc: t("home.advantages.item3Desc") },
              { icon: Users, title: t("home.advantages.item4Title"), desc: t("home.advantages.item4Desc") },
              { icon: Laptop, title: t("home.advantages.item5Title"), desc: t("home.advantages.item5Desc") },
              { icon: BarChart3, title: t("home.advantages.item6Title"), desc: t("home.advantages.item6Desc") },
            ].map((item) => (
              <div key={item.title} className="lp-card p-6 flex items-start gap-4">
                <span className="lp-chip w-11 h-11 shrink-0"><item.icon className="w-5 h-5 text-primary" /></span>
                <div>
                  <h3 className="lp-display text-lg mb-1">{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <p className="text-sm text-muted-foreground mb-3">{t("home.advantages.demoHint")}</p>
            <Link href="/demo">
              <Button variant="outline" size="lg" className="text-base px-8 h-12">
                {t("home.hero.demoPanels")}
                <ChevronRight className="w-5 h-5 ml-1.5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ───────── CTA ───────── */}
      <section className="py-16 sm:py-24">
        <div className="container">
          <div className="max-w-5xl mx-auto text-center p-8 sm:p-14 rounded-3xl gradient-primary relative overflow-hidden">
            <Ribbon className="lp-ribbon pointer-events-none absolute inset-0 w-full h-full opacity-30" />
            <div className="relative">
              <h2 className="lp-display text-3xl sm:text-4xl md:text-5xl text-white mb-4 leading-tight">{t("home.cta.title")}</h2>
              <p className="text-white/85 text-base sm:text-lg mb-8 max-w-2xl mx-auto leading-relaxed">{t("home.cta.subtitle")}</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link href="/register">
                  <Button size="lg" variant="secondary" className="text-base px-8 h-12 glow-primary-hover">
                    {t("home.cta.createAccount")}<ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <a href="mailto:judixkz@gmail.com">
                  <Button size="lg" variant="ghost" className="text-base px-8 h-12 text-white hover:text-white hover:bg-white/10">
                    {t("home.cta.askQuestion")}
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───────── Footer ───────── */}
      <footer className="py-12 border-t border-border">
        <div className="container">
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-8">
            <div>
              <Link href="/" className="flex items-center gap-2 mb-4">
                <img src="/logo.png" alt="JUDIX" className="h-9 w-auto" />
                <span className="lp-display text-xl font-semibold">JUDIX</span>
              </Link>
              <p className="text-muted-foreground text-sm leading-relaxed">{t("home.footer.description")}</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">{t("home.footer.platform")}</h4>
              <ul className="space-y-2 text-sm [&_a]:text-muted-foreground [&_a:hover]:text-foreground">
                <li><a href="#competitions">{t("home.footer.competitions")}</a></li>
                <li><Link href="/tournaments">{t("home.footer.catalog")}</Link></li>
                <li><a href="#pricing">{t("home.footer.pricing")}</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">{t("home.footer.support")}</h4>
              <ul className="space-y-2 text-sm [&_a]:text-muted-foreground [&_a:hover]:text-foreground">
                <li><a href="mailto:judixkz@gmail.com">judixkz@gmail.com</a></li>
                <li><Link href="/knowledge">{t("home.footer.knowledge")}</Link></li>
                <li><Link href="/tournaments">{t("home.footer.catalog")}</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">{t("home.footer.forOrganizers")}</h4>
              <ul className="space-y-2 text-sm [&_a]:text-muted-foreground [&_a:hover]:text-foreground">
                <li><Link href="/register">{t("home.footer.register")}</Link></li>
                <li><a href={getLoginUrl()}>{t("home.footer.login")}</a></li>
              </ul>
            </div>
          </div>
          {/* Партнёры (информационные): обычные ссылки, по договорённости — взаимные */}
          <div className="mt-10 flex flex-wrap items-baseline gap-x-6 gap-y-2 text-sm">
            <span className="font-semibold">{t("home.footer.partners")}</span>
            {PARTNERS.map(p => (
              <a key={p.url} href={p.url} target="_blank" rel="noopener" className="text-muted-foreground hover:text-foreground transition-colors">
                {t(p.labelKey)}
              </a>
            ))}
          </div>
          <div className="mt-8 pt-8 border-t border-border flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-muted-foreground text-sm">{t("home.footer.copyright", { year: new Date().getFullYear() })}</p>
            <p className="text-muted-foreground text-sm">{t("home.footer.madeIn")}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
